import { useState, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Button, Flex, Table, Tooltip } from "antd"
import { MailOutlined, UserOutlined, CloseOutlined } from "@ant-design/icons"
import { DSIcon } from "ui/Icon/DSIcon"
import { Phone } from "ui/Icon/DSIcon/Phone"
import useActiveGuide from "hooks/useActiveClient"
import CustomPagination from "components/common/CustomPagination"
import BookingConfirmationModal from "ui/BookingConfirmationModal"
import DeclineBookingModal from "../../BookingActions/DeclineBookingModal"
import { guideBookingStatus } from "store/bookings"
import TicketTypeIcon from "../TicketTypeIcon"
import ReservationFilterModal from "./ReservationFilterModal"
import SendMessageButton from "../../SendMessageButton"
import PaymentTag from "../../PaymentTag"
import BookingTag from "../../BookingTag"
import { Text } from "ui/Typography"
import { checkInGroup, checkOutGroup, resetGroup, resendGroupDocument } from "store/reservations"
import CheckInBadge from "../../CheckIn/CheckInBadge/CheckInBadge"
import CheckInGuestsModal from "../../CheckIn/CheckInGuestsModal/CheckInGuestsModal"
import IncompleteDocumentsModal from "../../CheckIn/IncompleteDocumentsModal/IncompleteDocumentsModal"
import { deriveStateFromGuests } from "../../CheckIn/checkInUtils"
import { useCheckInFlow } from "../../CheckIn/useCheckInFlow"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import CrewCell from "../CrewCell"
import ResourceCell from "../ResourceCell"
import { PAYMENT_CHIP_LABELS, EMPTY_FILTERS, EMPTY_SELECTION } from "./constants"
import styles from "./index.module.css"

const FilterChip = ({ label, onRemove }) => (
  <Flex align="center" gap={4} className={styles.filterChip}>
    <span className={styles.filterChipLabel}>{label}</span>
    <button type="button" className={styles.filterChipRemove} onClick={onRemove} aria-label={`Remove ${label} filter`}>
      <CloseOutlined />
    </button>
  </Flex>
)

FilterChip.propTypes = {
  label: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired
}

// No documents attached to the booking — leave the cell empty instead of showing "0/0",
// matching the Upcoming Experiences table.
const WaiverCell = ({ docsSign, docsTotal }) =>
  docsTotal === 0 ? null : (
    <BookingTag color={docsSign === docsTotal ? "success" : "error"}>
      <DSIcon name="file" width={14} height={14} fill="currentColor" />
      {docsSign}/{docsTotal}
    </BookingTag>
  )

WaiverCell.propTypes = {
  docsSign: PropTypes.number.isRequired,
  docsTotal: PropTypes.number.isRequired
}

const buildActiveChips = (filters, experienceLabelMap) => {
  const chips = []
  filters.experiences?.forEach((exp) => chips.push({ key: `exp:${exp}`, label: experienceLabelMap.get(exp) ?? exp }))
  if (filters.payment && PAYMENT_CHIP_LABELS[filters.payment]) {
    chips.push({ key: "payment", label: PAYMENT_CHIP_LABELS[filters.payment] })
  }
  if (filters.waivers === "signed") chips.push({ key: "waivers", label: "Signed" })
  if (filters.waivers === "not_signed") chips.push({ key: "waivers", label: "Not Signed" })
  if (filters.checkIn === "not_checked_in") chips.push({ key: "checkIn", label: "Not checked-in" })
  if (filters.checkIn === "checked_in") chips.push({ key: "checkIn", label: "Checked-in" })
  if (filters.checkIn === "checked_out") chips.push({ key: "checkIn", label: "Checked-out" })
  return chips
}


const ReservationListingTable = ({
  reservations = [],
  loading = false,
  status,
  onRefresh,
  metadata,
  page = 1,
  pageSize = 15,
  onPageChange,
  onPageSizeChange,
  sortKey,
  sortOrder,
  onSortChange,
  onViewBooking,
  onOpenFilters,
  filters = EMPTY_FILTERS,
  onFiltersChange,
  experienceOptions = [],
  /**
   * Selection lives in the page, not here: it is cleared when the lifecycle tab changes or the
   * list refetches, and a deep link will need to arrive with rows already selected. Keeping it
   * local meant the page's own resets ran against state nothing rendered, so a selection
   * survived a tab switch.
   */
  selectedRowKeys = EMPTY_SELECTION,
  setSelectedRowKeys
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isActive: isGuideActive } = useActiveGuide()
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  // Accept/Decline confirmation for the Requests tab: { record, action: "accept" | "decline" }
  const [confirmTarget, setConfirmTarget] = useState(null)
  // Accept/Decline in-flight state comes from Redux (guideBookingStatus → bookings.booking).
  const isSubmittingRequest = useSelector((store) => store.bookings.booking.loading)
  const isRequestsTab = status === "requested"
  const isCanceledTab = status === "canceled"
  const breakpoint = useBreakpoints()
  const isTabletOrMobile = ["xs", "xsm", "sm", "md", "lg"].includes(breakpoint)
  const checkIn = useCheckInFlow({
    checkIn: checkInGroup,
    checkOut: checkOutGroup,
    reset: resetGroup,
    resend: resendGroupDocument
  })
  const { getCheckedInCount, getCheckedOutCount, getGuests } = checkIn

  const openFilterModal = () => setFilterModalOpen(true)
  if (onOpenFilters) onOpenFilters.current = openFilterModal

  const data = useMemo(
    () => reservations.map((reservation) => {
      // override-aware per-guest list so the badge + modal reflect optimistic check-in/out
      const guests = getGuests(reservation)
      return {
        ...reservation,
        key: reservation.id,
        checkedInCount: getCheckedInCount(reservation),
        checkedOutCount: getCheckedOutCount(reservation),
        guests,
        checkInState: deriveStateFromGuests(guests)
      }
    }),
    [reservations, getGuests, getCheckedInCount, getCheckedOutCount]
  )

  // Sorting is backend-driven: the parent forwards sort_by/order to the reservations
  // fetch, so we only reflect the active column's direction here.
  const columnSortOrder = (columnKey) => {
    if (sortKey !== columnKey || !sortOrder) return null
    return sortOrder === "asc" ? "ascend" : "descend"
  }

  const experienceLabelMap = useMemo(
    () => new Map(experienceOptions.map((option) => [option.value, option.label])),
    [experienceOptions]
  )

  const activeChips = buildActiveChips(filters, experienceLabelMap)

  const selectedReservations = useMemo(
    () => data.filter((reservation) => selectedRowKeys.includes(reservation.key)),
    [data, selectedRowKeys]
  )

  // Drop any selected keys that no longer exist in the current data (e.g. after switching
  // month/tab/page/filters). Without this the selection bar lingers with stale, unchecked
  // rows — the checkboxes clear but `selectedRowKeys.length` still shows "N selected".
  useEffect(() => {
    setSelectedRowKeys((prev) => {
      const validKeys = new Set(data.map((reservation) => reservation.key))
      const pruned = prev.filter((key) => validKeys.has(key))
      return pruned.length === prev.length ? prev : pruned
    })
  }, [data, setSelectedRowKeys])

  const resultsCount = metadata?.total_results ?? reservations.length
  const resultsLabel = `${resultsCount} ${resultsCount === 1 ? "result" : "results"}`

  const removeChip = (key) => {
    if (key.startsWith("exp:")) {
      // Compared as strings: the experiences filter holds numbers once the option pool has
      // loaded, but raw strings before that — a blackout deep link seeds it straight from the
      // URL. `Number()` on either side silently failed to match in that window, so the chip
      // stayed put when clicked.
      const exp = key.slice(4)
      onFiltersChange?.((prev) => ({
        ...prev,
        experiences: prev.experiences.filter((experience) => String(experience) !== exp)
      }))
    } else {
      onFiltersChange?.((prev) => ({ ...prev, [key]: null }))
    }
  }

  const applyFilters = (newFilters) => {
    onFiltersChange?.(newFilters)
    setFilterModalOpen(false)
  }

  const clearAllFilters = () => {
    onFiltersChange?.((prev) => ({ ...EMPTY_FILTERS, experiences: prev.experiences }))
  }

  const openRequestConfirm = (record, action) => setConfirmTarget({ record, action })
  const closeRequestConfirm = () => setConfirmTarget(null)

  // Accept → "booked", Decline → "canceled" (same status_action endpoint the old listing used).
  // Refetch afterwards so the accepted/declined request drops off the Requests tab.
  // Accept → "booked". Decline → "canceled" with a required reason (surfaced to the client),
  // matching the reservation detail page's decline flow.
  const handleConfirmRequest = async (reason) => {
    if (!confirmTarget || isSubmittingRequest) return
    const status = confirmTarget.action === "accept" ? "booked" : "canceled"
    await dispatch(guideBookingStatus(confirmTarget.record.id, status, reason))
    closeRequestConfirm()
    onRefresh?.()
  }

  const columns = [
    {
      key: "inOut",
      title: t("bookings.columns.in_out"),
      width: 80,
      render: (_, record) => (
        <CheckInBadge
          state={record.checkInState}
          checkedInCount={record.checkedInCount}
          checkedOutCount={record.checkedOutCount}
          totalGuests={record.guestCount}
          onCheckIn={() => checkIn.openCheckIn(record, record.waiversComplete)}
          onCheckOut={() => checkIn.openCheckOut(record)}
          onReset={() => checkIn.reset(record)}
        />
      )
    },
    {
      key: "client",
      dataIndex: "client",
      title: t("bookings.columns.client"),
      width: 160,
      sorter: true,
      sortOrder: columnSortOrder("client"),
      render: (_, record) => (
        <Flex vertical gap={2}>
          <button
            type="button"
            className={styles.clientNameLink}
            onClick={() => onViewBooking?.(record)}
          >
            {record.name}
          </button>
          <Text variant="caption" size="md" color="var(--color-text-secondary-dark)">
            {record.bookingId}
          </Text>
        </Flex>
      )
    },
    {
      key: "contact",
      title: t("bookings.columns.client_contact"),
      render: (_, record) => (
        <Flex vertical gap={2}>
          {record.phone && (
            <Flex align="center" gap={6}>
              <Phone width={14} height={14} fill="var(--color-text-secondary-dark)" />
              <Text variant="caption" size="md" color="var(--color-text-primary)">{record.phone}</Text>
            </Flex>
          )}
          {record.email && (
            <Flex align="center" gap={6}>
              <MailOutlined className={styles.contactIcon} />
              <Text variant="caption" size="md" color="var(--color-text-primary)">{record.email}</Text>
            </Flex>
          )}
        </Flex>
      )
    },
    {
      // Backend sort keys: `booked_date` is the experience date, `created_date` is when the
      // booking was placed — so the sorters follow the values these columns now display.
      key: "booked_date",
      title: t("bookings.columns.date"),
      width: 120,
      sorter: true,
      sortOrder: columnSortOrder("booked_date"),
      render: (_, record) => (
        <Text variant="body" size="md" color="var(--color-text-primary)" className={styles.noWrap}>
          {record.experienceDate || "-"}
        </Text>
      )
    },
    {
      key: "experience",
      dataIndex: "experience",
      title: t("bookings.columns.experience"),
      sorter: true,
      sortOrder: columnSortOrder("experience"),
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <TicketTypeIcon isGroup={!!record.experienceIsGroup} />
          <Flex vertical gap={2}>
            <Text variant="body" size="md" fw={500} color="var(--color-text-primary)" className={styles.experienceTitle} title={record.experience}>
              {record.experience}
            </Text>
            {record.experienceTime && (
              <Text variant="caption" size="md" color="var(--color-text-secondary-dark)">{record.experienceTime}</Text>
            )}
          </Flex>
        </Flex>
      )
    },
    {
      key: "guests",
      title: t("bookings.columns.guests"),
      width: 80,
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <UserOutlined className={styles.guestIcon} />
          <Text variant="body" size="md" fw={500} color="var(--color-text-primary)">{record.guestCount}</Text>
        </Flex>
      )
    },
    {
      key: "created_date",
      title: t("bookings.columns.booked"),
      width: 170,
      sorter: true,
      sortOrder: columnSortOrder("created_date"),
      render: (_, record) => (
        <Text variant="body" size="md" color="var(--color-text-primary)" className={styles.noWrap}>
          {record.createdDate || "-"}
        </Text>
      )
    },
    {
      key: "payment",
      dataIndex: "payment",
      width: 180,
      title: t("bookings.columns.payment"),
      sorter: true,
      sortOrder: columnSortOrder("payment"),
      render: (_, record) => (
        <Flex vertical align="flex-start" gap={4} className="w-max">
          {record.paymentTag && (
            <PaymentTag label={record.paymentTag.label} color={record.paymentTag.color} />
          )}
          {record.collectionTag && (
            <PaymentTag label={record.collectionTag.label} color={record.collectionTag.color} />
          )}
        </Flex>
      )
    },
    {
      key: "waivers",
      dataIndex: "waivers",
      title: t("bookings.columns.digital_docs"),
      width: 120,
      sorter: true,
      sortOrder: columnSortOrder("waivers"),
      render: (_, record) => (
        <span className={styles.docsBadgeSlot}>
          <WaiverCell docsSign={record.docsSign ?? 0} docsTotal={record.docsTotal ?? 0} />
        </span>
      )
    },
    {
      // Inline crew/resource assignment, mirroring the By Experience table. The reservation's own
      // uuid is the booking identifier the assignment endpoints key off (same id the detail
      // page's ResourcesPanel passes).
      key: "crew",
      title: t("bookings.columns.crew"),
      width: 180,
      render: (_, record) => <CrewCell initialCrew={record.crew || []} bookingId={record.id} />
    },
    {
      key: "resources",
      title: t("bookings.columns.resources"),
      width: 260,
      render: (_, record) => <ResourceCell initialResources={record.resources || []} bookingId={record.id} />
    },
    {
      key: "actions",
      width: 60,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => onViewBooking?.(record)}>
          View
        </Button>
      )
    }
  ]

  // Requests tab (Figma): no In/Out or Digital Docs columns, and the row action is Accept/Decline
  // instead of View — the guide approves or declines the pending request inline.
  const requestActionsColumn = {
    key: "requestActions",
    width: 200,
    // Same gating as the reservation detail page: an auto-expired request (trip time has passed)
    // and a deactivated guide can't accept or decline. Only expiry gets a tooltip.
    render: (_, record) => {
      const isDisabled = record.isExpired || !isGuideActive
      return (
        <Tooltip title={record.isExpired ? t("client.client_booking.trip_time_has_passed") : ""} placement="top" mouseEnterDelay={0.3}>
          <Flex gap={8} justify="flex-end">
            <Button
              color="primary"
              variant="outlined"
              shape="round"
              size="small"
              disabled={isDisabled}
              onClick={() => openRequestConfirm(record, "decline")}
            >
              Decline
            </Button>
            <Button type="primary" shape="round" size="small" disabled={isDisabled} onClick={() => openRequestConfirm(record, "accept")}>
              Accept
            </Button>
          </Flex>
        </Tooltip>
      )
    }
  }

  // Requests: no In/Out and no Digital Docs, and the row action is Accept/Decline instead of View.
  // Canceled: no In/Out (canceled bookings can't be checked in/out). Neither tab gets the
  // crew/resource cells — there is nothing to staff on a pending request or a canceled booking.
  let visibleColumns = columns
  if (isRequestsTab) {
    visibleColumns = columns
      .filter((column) => !["inOut", "waivers", "crew", "resources", "actions"].includes(column.key))
      .concat(requestActionsColumn)
  } else if (isCanceledTab) {
    visibleColumns = columns.filter((column) => !["inOut", "crew", "resources"].includes(column.key))
  }
  // Crew/Resources need room to breathe; drop them on tablet widths like the By Experience table.
  if (isTabletOrMobile) visibleColumns = visibleColumns.filter((column) => !["crew", "resources"].includes(column.key))

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    columnWidth: 40
  }

  return (
    <Flex vertical className={styles.container}>
      {/* The count states what the list is showing and is true with or without filters — it used
          to live inside the chips row, so it vanished the moment the last chip was removed. */}
      <Flex align="center" gap={8} wrap="wrap" className={styles.filterChipsRow}>
        {activeChips.map((chip) => (
          <FilterChip key={chip.key} label={chip.label} onRemove={() => removeChip(chip.key)} />
        ))}
        <Text variant="body" size="md" fw={500} color="var(--color-text-primary)">
          {resultsLabel}
        </Text>
      </Flex>

      {selectedRowKeys.length > 0 && (
        <Flex align="center" gap={8} className={styles.selectionBar}>
          <Text variant="body" size="md" fw={500} color="var(--color-text-primary)">{selectedRowKeys.length} selected</Text>
          <SendMessageButton
            recipients={selectedReservations}
            label="Message Clients"
            buttonProps={{ type: "primary", size: "small", shape: "round" }}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <button
            type="button"
            className={styles.clearSelectionBtn}
            onClick={() => setSelectedRowKeys([])}
            aria-label="Clear selection"
          >
            <CloseOutlined />
          </button>
        </Flex>
      )}

      <Table
        columns={visibleColumns}
        dataSource={data}
        loading={loading}
        rowSelection={rowSelection}
        rowKey="key"
        pagination={false}
        onChange={onSortChange}
        scroll={{ x: "max-content" }}
      />

      <CustomPagination
        metadata={metadata || {
          page,
          total_pages: Math.ceil((reservations.length || 0) / pageSize) || 1,
          next_page: null,
          prev_page: null
        }}
        pageSize={pageSize}
        total={metadata?.total_results ?? reservations.length}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={[15, 25, 50, 100]}
      />

      <ReservationFilterModal
        open={filterModalOpen}
        filters={filters}
        onApply={applyFilters}
        onClear={clearAllFilters}
        onClose={() => setFilterModalOpen(false)}
      />

      <IncompleteDocumentsModal
        open={!!checkIn.incompleteTarget}
        onClose={checkIn.closeIncomplete}
        onOverride={checkIn.handleOverride}
      />

      <CheckInGuestsModal
        open={!!checkIn.modalItem}
        onClose={checkIn.closeModal}
        bookingId={checkIn.modalItem?.bookingId}
        clientName={checkIn.modalItem?.name}
        paymentTag={checkIn.modalItem?.paymentTag}
        collectionTag={checkIn.modalItem?.collectionTag}
        guests={checkIn.getGuests(checkIn.modalItem)}
        isResending={checkIn.isResending}
        onToggleGuest={(guest) => checkIn.toggleGuest(checkIn.modalItem, guest)}
        onResend={(guest, document) => checkIn.resendDocument(checkIn.modalItem, guest, document)}
      />

      {confirmTarget?.action === "accept" && (
        <BookingConfirmationModal
          open
          bookingConfirmStatus="accept"
          clientName={confirmTarget.record.name}
          tripDate={confirmTarget.record.experienceDate}
          handleCancel={closeRequestConfirm}
          handleConfirm={() => handleConfirmRequest()}
          loading={isSubmittingRequest}
        />
      )}

      {confirmTarget?.action === "decline" && (
        <DeclineBookingModal
          open
          clientName={confirmTarget.record.name}
          tripDate={confirmTarget.record.experienceDate}
          onCancel={closeRequestConfirm}
          onConfirm={(reason) => handleConfirmRequest(reason)}
          isSubmitting={isSubmittingRequest}
        />
      )}
    </Flex>
  )
}

ReservationListingTable.propTypes = {
  reservations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      bookingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      experience: PropTypes.string,
      experienceDate: PropTypes.string,
      experienceTime: PropTypes.string,
      guestCount: PropTypes.number,
      checkInState: PropTypes.oneOf(["pending", "in", "out"]),
      paymentTag: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
      collectionTag: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
      docsSign: PropTypes.number,
      docsTotal: PropTypes.number,
      crew: PropTypes.array,
      resources: PropTypes.array
    })
  ),
  loading: PropTypes.bool,
  status: PropTypes.string,
  onRefresh: PropTypes.func,
  /** Selection is owned by the page — see the prop comment above. */
  selectedRowKeys: PropTypes.array,
  setSelectedRowKeys: PropTypes.func.isRequired,
  metadata: PropTypes.object,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  sortKey: PropTypes.string,
  sortOrder: PropTypes.string,
  onSortChange: PropTypes.func,
  onViewBooking: PropTypes.func,
  onOpenFilters: PropTypes.shape({ current: PropTypes.func }),
  filters: PropTypes.shape({
    payment: PropTypes.string,
    waivers: PropTypes.string,
    checkIn: PropTypes.string,
    experiences: PropTypes.arrayOf(PropTypes.number)
  }),
  onFiltersChange: PropTypes.func,
  experienceOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }))
}

export default ReservationListingTable
